import React, { useEffect, Suspense } from "react";
import { useParams } from "react-router-dom";
import { EncounterHeader } from "./EncounterOverview";
import { Q_ZONE, read_id, TierSelect } from "./Details";
import { gql, useQuery } from "@apollo/client";
import { formatDuration } from "./format";
import { Encounter } from "./model";
import { EncounterBreakdownPlaceHolder } from "./placeholders";

const ENCOUNTER_STATS = gql`
  query EncounterStats($encounterId: Int!, $difficulty: Int!) {
    encounterStatOverview(encounterId: $encounterId, difficulty: $difficulty) {
      pullCount
      progTime
      startIlvl
      endIlvl
      startTime
      killTime
      killNumber
    }
    encounterStatSummary(encounterId: $encounterId, difficulty: $difficulty) {
      killCount
      pullsRange
      progTimeRange
    }
    hotfixes(encounterId: $encounterId) {
      applicationDate
    }
  }
`;

const EncounterBreakdownChart = React.lazy(
  () => import("./charts/EncounterBreakdown")
);

interface EncounterStatSummary {
  killCount: number;
  pullsRange: [number, number];
  progTimeRange: [number, number];
}

const DtTitle = ({ text, tooltip }: { text: string; tooltip: string }) => (
  <dt
    title={tooltip}
    className="cursor-help underline"
    style={{
      textDecorationStyle: "dotted",
      textDecorationThickness: "from-font",
    }}
  >
    {text}
  </dt>
);

function EncounterStatOverview({
  killCount,
  pullsRange,
  progTimeRange,
}: EncounterStatSummary) {
  return (
    <div className="w-max">
      <dl className="grid gap-x-4" style={{ gridTemplateColumns: "auto auto" }}>
        <dt>Recorded Kills:</dt>
        <dd>{killCount}</dd>
        <DtTitle text="Typical Pull Count:" tooltip="25th to 75th Percentile" />
        <dd>
          {pullsRange[0]} &mdash; {pullsRange[1]}
        </dd>
        <DtTitle
          text="Typical Progression Time:"
          tooltip="25th to 75th Percentile"
        />
        <dd>
          {formatDuration(progTimeRange[0], "")} &mdash;{" "}
          {formatDuration(progTimeRange[1])}
        </dd>
      </dl>
    </div>
  );
}

export function EncounterBreakdown({
  encounter,
  difficulty,
}: {
  encounter: Encounter;
  difficulty: number;
}) {
  const encounterId = encounter.id;
  const { data, error } = useQuery(ENCOUNTER_STATS, {
    variables: { encounterId, difficulty },
  });

  let content = null;
  if (error) {
    content = <p>Error: {error.message}</p>;
  } else if (data?.encounterStatSummary?.killCount === 0) {
    content = (
      <p>
        <em>No recorded kills.</em>
      </p>
    );
  } else if (data && data.encounterStatOverview) {
    content = (
      <>
        <EncounterStatOverview {...data.encounterStatSummary} />
        <Suspense fallback={<EncounterBreakdownPlaceHolder />}>
          <EncounterBreakdownChart
            stats={data.encounterStatOverview}
            hotfixes={data.hotfixes}
          />
        </Suspense>
      </>
    );
  } else {
    content = (
      <>
        <div />
        <EncounterBreakdownPlaceHolder />
      </>
    );
  }

  return (
    <section className="mt-5 mb-5 animate-fadein">
      <EncounterHeader encounter={encounter} difficulty={difficulty} />
      <div className="grid gap-8 xl:grid-cols-encounter mt-2">{content}</div>
    </section>
  );
}

export default function TierDetails() {
  const { zone } = useParams<{ zone: string }>();
  const tier = read_id(zone);

  const { data: zoneData } = useQuery(Q_ZONE, {
    variables: { zone: tier },
    skip: !tier,
  });

  const name = zoneData?.encounters.name;
  const encounters = zoneData?.encounters.encounters;

  useEffect(() => {
    if (name) {
      document.title = `${name} Overview | WoW Progression Stats`;
    }
  }, [name]);

  if (!tier || !encounters) {
    return null;
  }

  return (
    <div className="container mx-auto mt-10">
      <TierSelect
        tier={tier}
        className="text-3xl md:text-5xl"
        chevronClassName="text-2xl md:text-4xl ml-2 md:ml-4"
      />
      {encounters.map((encounter: Encounter) => (
        <EncounterBreakdown
          key={encounter.id}
          encounter={encounter}
          difficulty={5}
        />
      ))}
    </div>
  );
}
